import { CSSProperties } from 'react';
import { fillTextBox, measureText } from '@remotion/layout-utils';
import { Page } from '../../../types';
import { DEFAULT_FONT_SIZE, StylesForPage } from '../styles';
import { CaptionTransitions } from '../../transitions/types';
import { CaptionStyles, SizeType } from '@/domains/asset';
import { Word } from '@/domains/transcript';
import { FONT_DEFAULT_VALUE } from '@/Pages/Clip/SideBar/FontSelector/constants';
import { CAPTIONS_PAGE_GAP_THRESHOLD_SECONDS, COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@/App/remotion/constants';
import { parsePxValue } from '@/App/remotion/components/Captions/CaptionsMenuV2/utils';

const CONTAINER_WIDTH_BUFFER_PX = 128;

function shouldBreakOnPunctuation(word: string): boolean {
  const trimmedWord = word.trim();
  return /[.!?]$/.test(trimmedWord);
}

function isSpeakerChange(currentIndex: number, captions: Word[]): boolean {
  const currentWord = captions[currentIndex];
  const nextWord = captions[currentIndex + 1];

  if (!nextWord) {
    return false;
  }

  return Boolean(
    currentWord.speaker_label && nextWord.speaker_label && currentWord.speaker_label !== nextWord.speaker_label
  );
}

function shouldBreakOnLongGap(currentIndex: number, captions: Word[]): boolean {
  const currentWord = captions[currentIndex];
  const nextWord = captions[currentIndex + 1];

  if (!nextWord) {
    return false;
  }

  const currentWordEndTime = currentWord.processed_end_time || 0;
  const nextWordStartTime = nextWord.processed_start_time || 0;
  const gap = nextWordStartTime - currentWordEndTime;

  return gap > CAPTIONS_PAGE_GAP_THRESHOLD_SECONDS;
}

function eliminatePageGap(pages: Page[], fps: number): Page[] {
  const pageGapThresholdFrames = Math.ceil(fps * CAPTIONS_PAGE_GAP_THRESHOLD_SECONDS);

  return pages.map((page, i) => {
    const nextPage = pages[i + 1];
    if (!nextPage) {
      return page;
    }

    const naturalGap = nextPage.fromFrame - page.toFrame;

    if (naturalGap <= pageGapThresholdFrames) {
      return { ...page, toFrame: nextPage.fromFrame };
    }

    return { ...page, toFrame: page.toFrame + pageGapThresholdFrames };
  });
}

export function createLineCountPages({
  captions,
  fps,
  captionStyles,
  size,
  maxLines,
  stylesForPage,
  fontFamily,
  transitions
}: {
  captions: Word[];
  fps: number;
  captionStyles: CaptionStyles | undefined;
  size: SizeType;
  maxLines: number;
  stylesForPage: StylesForPage;
  fontFamily?: string;
  transitions?: CaptionTransitions;
}): Page[] {
  let pages: Page[] = [];

  const fontSize = captionStyles?.font_size || (captionStyles?.scale || 1) * DEFAULT_FONT_SIZE;
  const fontName = fontFamily || FONT_DEFAULT_VALUE.name;
  const isCaps = captionStyles?.caps || false;

  const pagePaddingPx =
    parsePxValue(captionStyles?.pageStyle?.paddingLeft) + parsePxValue(captionStyles?.pageStyle?.paddingRight) || 0;
  const canvasWidth = COMPOSITION_DIMENSIONS[size][VIDEO_QUALITY.FULL_HD].WIDTH;
  const containerWidthPercentString = stylesForPage.style?.width?.toString().split('%')[0];
  const containerWidthPercent = containerWidthPercentString ? parseFloat(containerWidthPercentString) : 95;
  const containerWidthPx = (containerWidthPercent / 100) * canvasWidth - CONTAINER_WIDTH_BUFFER_PX - pagePaddingPx;

  let currentPageWords: Word[] = [];
  let currentPageTokens: Page['tokens'] = [];
  let layoutUtilsTextBox = fillTextBox({ maxBoxWidth: containerWidthPx, maxLines });

  function isLongWord(word: Word) {
    const wordText = isCaps ? word.content.toUpperCase() : word.content;
    const wordWidth = measureText({
      text: wordText,
      fontFamily: fontFamily || FONT_DEFAULT_VALUE.name,
      fontSize
    }).width;

    return wordWidth > containerWidthPx;
  }

  function createPageFromCurrentContent() {
    if (currentPageWords.length === 0) {
      return;
    }

    const fromFrame = currentPageTokens[0].fromFrame;
    const toFrame = currentPageTokens[currentPageTokens.length - 1].toFrame;
    const pageText = currentPageWords.map(w => w.content).join(' ');

    pages.push({
      text: pageText,
      tokens: currentPageTokens,
      fromFrame,
      toFrame,
      transitions,
      ...stylesForPage
    });

    currentPageWords = [];
    currentPageTokens = [];
    layoutUtilsTextBox = fillTextBox({ maxBoxWidth: containerWidthPx, maxLines });
  }

  function createPageFromLongWord(word: Word) {
    const longWordStyles = {
      ...stylesForPage,
      style: {
        ...stylesForPage.style,
        hyphens: 'auto',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        textAlign: 'center'
      } as CSSProperties
    };

    pages.push({
      text: isCaps ? word.content.toUpperCase() : word.content,
      tokens: [
        {
          text: word.content,
          fromFrame: Math.ceil((word.processed_start_time || 0) * fps),
          toFrame: Math.ceil((word.processed_end_time || 0) * fps)
        }
      ],
      fromFrame: Math.ceil((word.processed_start_time || 0) * fps),
      toFrame: Math.ceil((word.processed_end_time || 0) * fps),
      transitions,
      ...longWordStyles
    });
  }

  for (let i = 0; i < captions.length; i++) {
    const word = captions[i];

    if (word.hidden) {
      continue;
    }

    if (isLongWord(word)) {
      createPageFromCurrentContent();
      createPageFromLongWord(word);
      continue;
    }

    const wordText = isCaps ? word.content.toUpperCase() : word.content;

    const result = layoutUtilsTextBox.add({
      text: `${wordText} `,
      fontFamily: fontName,
      fontSize
    });

    if (result.exceedsBox) {
      createPageFromCurrentContent();

      // Add the word to the new box
      layoutUtilsTextBox.add({ text: `${wordText} `, fontFamily: fontName, fontSize });
    }

    currentPageWords.push(word);
    currentPageTokens.push({
      text: word.content,
      fromFrame: Math.ceil((word.processed_start_time || 0) * fps),
      toFrame: Math.ceil((word.processed_end_time || 0) * fps)
    });

    const shouldManuallyCreateNewPage =
      shouldBreakOnPunctuation(word.content) || isSpeakerChange(i, captions) || shouldBreakOnLongGap(i, captions);

    if (shouldManuallyCreateNewPage) {
      createPageFromCurrentContent();
    }
  }

  createPageFromCurrentContent();

  pages = eliminatePageGap(pages, fps);

  return pages;
}
